import { colors } from 'Phaser/config';
import ExerciseBase, { IObjective } from './ExerciseBase';
import Cursor, { StaffNote, StartTiming } from 'Phaser/GameObjects/CursorLegacy';
import { Wipe } from 'Phaser/Rendering/Transitions';
import TimeSignature from 'Models/TimeSignature';
import IPlayable from 'Phaser/GameObjects/Playable';
import { ProgressState } from 'Phaser/GameObjects/Progress';
import { INoteGraphicsDatabase } from './StaffExercise';
import { COLORBLIND_TYPE, ERROR_REC_CLASS, isCapacitorNative } from 'Utils/Constants';
import { getColorByCbType } from 'Components/Colors';
import MuseFlowAudio from 'Utils/MuseFlowAudioPlugin';
import { NoteType } from '@museflow-ai/opensheetmusicdisplay';
import { NotifyReactEvents } from 'Phaser/EventBus2';
import { SuspendGuard } from 'Utils/suspendGuard';
import { tutorialCountInBeats } from 'Utils/playAlong/tutorialPlayAlong';
import { ringAround } from 'Phaser/Gameplay/CountInCountdownView';

interface TempoObjective extends IObjective {
  numBeats: number;
  tempo: number;
  instructionWaitTime?: number;
}

export interface Config {
  chapterIdx?: number;
  levelNumber?: number;
  paddingVertical?: number;
  paddingHorizontal?: number;
  cbType?: COLORBLIND_TYPE;
  objectives: TempoObjective[];
}

type BeatState = 'hit' | 'miss' | 'late' | 'idle';

class Beat implements IPlayable {
  isFocused: boolean = false;
  graphic: Phaser.GameObjects.Text;
  state: BeatState = 'idle';
  tweenHandle?: Phaser.Tweens.Tween;
  scene: Phaser.Scene;
  resetColorTimer?: Phaser.Time.TimerEvent;
  tempo: number;
  cbType?: COLORBLIND_TYPE;

  constructor(
    graphic: Phaser.GameObjects.Text,
    scene: Phaser.Scene,
    tempo: number,
    cbType?: COLORBLIND_TYPE,
  ) {
    this.cbType = cbType;
    this.graphic = graphic;
    this.scene = scene;
    this.tempo = tempo;
  }

  getHaloOffset() {
    return 0;
  }

  getState() {
    switch (this.state) {
      case 'hit':
        return ProgressState.Ended;
      case 'late':
        return ProgressState.LateStart;
      case 'miss':
        return ProgressState.Missed;
    }
  }

  update(_: number) {}
  setColor(_color: number | string) {}
  pulse() {}

  setTimingState(state: ProgressState) {
    switch (state) {
      case ProgressState.TimelyStart:
        this.state = 'hit';
        this.graphic.setColor(getColorByCbType(ERROR_REC_CLASS.CORRECT, this.cbType ?? null));
        break;
      case ProgressState.LateStart:
        this.state = 'late';
        this.graphic.setColor(getColorByCbType(ERROR_REC_CLASS.SEMI_ERROR, this.cbType ?? null));
        break;
      case ProgressState.Missed:
        this.graphic.setColor(getColorByCbType(ERROR_REC_CLASS.ERROR, this.cbType ?? null));
        this.state = 'miss';
        break;
      default:
        break;
    }
  }

  getPosition() {
    return { x: this.graphic.x, y: this.graphic.y };
  }

  reset(forceColor: boolean = false) {
    this.isFocused = false;
    this.tweenHandle?.destroy();
    this.resetColorTimer?.remove();
    if (forceColor) this.graphic.setColor(colors.beatNumberFillQuestion);
    else {
      this.resetColorTimer = this.scene.time.delayedCall(
        2500,
        () => this.graphic.setColor(colors.beatNumberFillQuestion),
        [],
        this,
      );
    }
  }

  setFocus(focus: boolean): void {
    this.resetColorTimer?.remove();
    this.isFocused = focus;
    if (!focus) return this.tweenHandle?.destroy();
    this.tweenHandle = this.scene.tweens.add({
      targets: this.graphic,
      scale: 1.3,
      x: this.graphic.x - this.graphic.width * 0.15,
      y: this.graphic.y - this.graphic.height * 0.15,
      ease: Phaser.Math.Easing.Quadratic.InOut,
      yoyo: true,
      duration: 150,
    });
  }

  shake(): void {
    this.tweenHandle?.remove();
    const target = this.graphic;
    const pos = target.x;
    const dpr = 1;
    const duration = Math.min((60 / this.tempo) * 1000, 200);
    this.tweenHandle = this.scene.tweens.add({
      targets: { value: 0 },
      value: Math.PI * 2,
      duration,
      ease: Phaser.Math.Easing.Sine.In,
      repeat: 1,
      onUpdate: (_tween, _target, _key, current) => {
        target.setX(pos + Math.sin(current) * 10 * dpr);
      },
    });
  }

  destroy() {
    this.isFocused = false;
    this.tweenHandle?.destroy();
    this.resetColorTimer?.remove();
    this.graphic?.destroy();
  }
}

export class ExerciseTempoPractice
  extends ExerciseBase<TempoObjective, Config>
  implements INoteGraphicsDatabase
{
  table: Map<string, IPlayable>;
  cursor?: Cursor;
  playAreaSize?: { width: number; height: number; x: number; y: number };
  beatGraphics: Phaser.GameObjects.Text[];
  currentBeat: number;
  beatState: BeatState[];
  beats: Beat[];
  beatTweenHandle: Phaser.Tweens.Tween[] = [];
  running: boolean = false;
  started: boolean = false;
  history: BeatState[] = [];
  private nativeMetronomeStarted = false;

  // MUS-552: guards against the free-running audio clock false-completing this
  // exercise on a device suspend/resume (same bug class MUS-175 fixed for
  // StaffExercise). Sampled each active frame in update(), re-baselined at
  // play-start and on resume.
  private readonly suspendGuard = new SuspendGuard();

  constructor() {
    super('ExerciseTempoPractice');
    this.beatGraphics = [];
    this.beatState = [];
    this.beats = [];
    this.currentBeat = -1;
    this.running = false;
    this.table = new Map();
  }

  // ==================================================================
  // Scene lifecycle
  // ==================================================================

  init(config: Config) {
    this.nativeMetronomeStarted = false;
    // MUS-552: Phaser reuses this scene object, and a run that is cut short
    // (chapter change or skip mid-phrase) never reaches stopPlayback(). Left
    // set, `running` would have update() sample the guard against the old run's
    // baseline and open the chapter paused on its first frame.
    this.running = false;
    super.init(config);
  }

  create() {
    try {
      this.cameras.main.setPostPipeline('Wipe');
      NotifyReactEvents.CurrentSceneReady(this);
    } catch (err) {
      console.error('ExerciseTempoPractice.create failed', err);
    }
  }

  /** Ungraded: the count-in's countdown, from the cursor's click count. */
  private showCountInCountdown(): void {
    // The ring closes on the first beat's number.
    this.updateCountInCountdown(this.cursor?.countInProgress() ?? null, this.cursor ?? null, () =>
      this.beatGraphics[0] ? ringAround(this.beatGraphics[0]) : null,
    );
  }

  update(time: number) {
    // MUS-552: hold every frame while paused, not only the one that paused.
    // CursorLegacy.update() moves the cursor and fires its end callback outside
    // its own `paused` checks, and its resume compensation assumes it was not
    // called during the pause, so a paused scene must never reach it.
    if (this.paused) {
      this.showCountInCountdown();
      return;
    }
    // MUS-552: a device suspend/resume produces a single frame whose wall-clock
    // delta is the whole away-duration. CursorLegacy.update() advances off the
    // free-running audio clock and completes the exercise when it runs past the
    // last note, so left unguarded that jump false-completes with no user input.
    // Sample only while actively playing and not paused; when the jump lands,
    // pause and return BEFORE cursor.update() so the completion check never runs
    // (prevented, not just detected). Resume re-anchors the clock and re-baselines
    // the guard, so the stale delta is discarded there.
    if (this.running && this.suspendGuard.sample()) {
      this.dispatchExerciseEvent({ type: 'PAUSE_EXERCISE' });
      return;
    }
    this.cursor?.update(time);
    // Read after the cursor ticks, so each number changes on its click's frame.
    this.showCountInCountdown();
  }

  // ==================================================================
  // IExerciseSceneFacade overrides
  // ==================================================================

  loadObjective(objectiveIndex: number): void {
    this.currentObjective = objectiveIndex;
    const objective = this.config?.objectives[objectiveIndex];
    if (objective) {
      this.showInstruction(
        objective.text ?? 'Use any key to tap out the beats in sync with the tempo.',
      );
      this.showTapHint();
    }
    this.instantiateObjects();
    this.wipe(false, () => {
      this.dispatchExerciseEvent({ type: 'OBJECTIVE_LOADED' });
    });
  }

  initExercise(_objectiveIndex: number): void {
    if (!this.beats || this.beats.length === 0) {
      console.warn('[ExerciseTempoPractice] initExercise: no beats loaded');
      return;
    }
    this.started = true;
    this.cursor?.reset();
    this.running = false;
    this.history = [];
    this.beats.forEach((beat) => beat.reset());
    this.currentBeat = -1;
    if (!this.graded && this.cursor) {
      this.countInAndPlayAlong(this.cursor);
      return;
    }
    this.cursor?.waitForInput();
  }

  /**
   * Ungraded: nothing waits for a first tap. A 1-bar count-in clicks, then the
   * cursor walks the beats on its own; with nothing heard, no beat is marked
   * missed.
   */
  private countInAndPlayAlong(cursor: Cursor) {
    cursor.marksMissedNotes = this.passWasHeard();
    this.startPlayAlongPass((continuesLoop) => {
      if (this.cursor !== cursor || cursor.isActive()) return;
      const countIn = continuesLoop
        ? cursor.continueCountIn.bind(cursor)
        : cursor.startAfterCountIn.bind(cursor);
      countIn(
        tutorialCountInBeats(1, 1 / 4),
        () => {
          this.beatGraphics.forEach((graphic) => graphic.setColor(colors.beatNumberFillQuestion));
          this.running = true;
          this.suspendGuard.reset();
          this.dispatchExerciseEvent({ type: 'AUTO_START' });
        },
        this.onExerciseEnd,
        this,
      );
      if (!continuesLoop) cursor.waitForInput();
    });
  }

  protected loopsWhilePracticing(): boolean {
    return true;
  }

  evaluatePerformance(): void {
    if (!this.passWasHeard()) {
      this.dispatchExerciseEvent({ type: 'RUN_ENDED' });
      return;
    }
    const hasFails = this.beats.some(
      (beat) => beat.state === 'miss' || beat.state === 'late' || beat.state === 'idle',
    );
    if (hasFails) {
      this.dispatchExerciseEvent({ type: 'OBJECTIVE_FAILED' });
    } else {
      this.dispatchExerciseEvent({ type: 'OBJECTIVE_PASSED' });
    }
  }

  stopPlayback(): void {
    this.running = false;
    this.started = false;
    this.cursor?.stop();
  }

  showFailAnimation(_failedAttempts: number): void {
    this.running = false;
    this.failExercise();
  }

  pauseScene(): void {
    this.cursor?.pause();
    this.paused = true;
    this.scene.launch('PauseMenu');
    NotifyReactEvents.ExercisePaused({});
  }

  resumeScene(): void {
    this.paused = false;
    this.suspendGuard.reset(); // MUS-552: consume the away-gap; next real sleep still caught
    this.cursor?.resume();
    NotifyReactEvents.ExerciseResumed();
  }

  unloadGraphics(): void {
    this.clear();
  }

  // ==================================================================
  // Native metronome
  // ==================================================================

  private startNativeMetronome(bpm: number) {
    if (!isCapacitorNative() || this.nativeMetronomeStarted) return;
    this.nativeMetronomeStarted = true;
    MuseFlowAudio.setMetronomeEnabled({ enabled: true });
    MuseFlowAudio.setDownbeatsEnabled({ enabled: true });
    MuseFlowAudio.startPlayback({
      bpm,
      timeSigNumerator: 4,
      timeSigDenominator: 4,
      countInMeasures: 0,
      startMeasure: 0,
      tickNoteType: NoteType._32nd,
    });
  }

  private stopNativeMetronome() {
    if (!isCapacitorNative() || !this.nativeMetronomeStarted) return;
    this.nativeMetronomeStarted = false;
    MuseFlowAudio.pausePlayback();
  }

  // ==================================================================
  // Wipe transition
  // ==================================================================

  private wipe(reverse?: boolean, onComplete?: () => void): void {
    const targets = this.cameras.main.getPostPipeline('Wipe') as Wipe;
    this.tweens.add({
      targets,
      progress: reverse ? 0.0 : 1.0,
      duration: 1000,
      onComplete: () => onComplete?.(),
    });
  }

  // ==================================================================
  // Beat objects
  // ==================================================================

  private clear() {
    this.beatGraphics.forEach((g) => g.destroy());
    this.beatState = [];
    this.history = [];
    this.beatGraphics = [];
    this.beats.forEach((b) => b.destroy());
    this.beats = [];
    this.cursor?.destroy();
    this.started = false;
  }

  private instantiateObjects() {
    const dpr = 1;
    const paddingH = (this.config?.paddingHorizontal ?? 50) * dpr;
    const paddingV = (this.config?.paddingVertical ?? 50) * dpr;
    this.playAreaSize = {
      width: this.cameras.main.width - paddingH * 2,
      height: this.cameras.main.height - paddingV * 2,
      x: paddingH,
      y: paddingV,
    };

    const objectiveIndex = Math.max(this.currentObjective ?? 0, 0);
    if (!this.config || objectiveIndex >= this.config.objectives.length) return;

    const objective = this.config.objectives[objectiveIndex];
    if (!objective) return;

    const { tempo, numBeats } = objective;
    const yMid = this.playAreaSize.height / 2;
    const beatWidth = this.playAreaSize.width / (numBeats + 1);
    const offset = this.playAreaSize.x;

    for (let i = 0; i < numBeats; i++) {
      const graphic = this.add.text(
        (i + 1) * beatWidth + offset + 3 * dpr,
        yMid - 30 * dpr,
        (i + 1).toString(),
        {
          fontFamily: 'Lato',
          fontStyle: 'bold',
          fontSize: 85 * dpr,
          color: colors.beatNumberFillQuestion,
        },
      );
      graphic.setX(graphic.x - graphic.displayWidth / 2);
      this.beatGraphics.push(graphic);
      this.beats.push(new Beat(graphic, this, tempo, this.config?.cbType));
    }

    const beatDuration = 60000 / tempo;
    const cursorData: StaffNote[] = this.beats.map((beat) => ({
      position: { x: beat.graphic.x, y: beat.graphic.y },
      durationInMs: beatDuration,
      durationRelative: 0.25,
      playable: beat,
    }));
    const endPos = {
      x: this.playAreaSize.width + this.playAreaSize.x,
      y: yMid - 30 * dpr,
    };
    cursorData.push({ position: endPos, durationInMs: 0, durationRelative: 0 });

    this.cursor = new Cursor(
      this,
      offset + beatWidth - this.beatGraphics[0].displayWidth / 2,
      yMid - 150 * dpr,
      45 * dpr,
      300 * dpr,
      20 * dpr,
      new TimeSignature(4, 4),
      tempo,
      cursorData,
    );
    this.allowForTouchLatency(this.cursor);
  }

  // ==================================================================
  // MIDI input
  // ==================================================================

  midiOn(data?: { note: number; velocity?: number; tap?: boolean }): void {
    // MUS-552: a paused exercise takes no keys (as StaffExercise), so a key can
    // never start a run that update() is holding still.
    if (this.paused || !this.hearsNote(data)) return;
    if (!this.started) return;
    if (this.graded) this.onPressKey();
    else this.onTap();
  }

  protected takesTaps(): boolean {
    return true;
  }

  /**
   * Tap input: the run starts on its own after the count-in, so a tap never
   * starts it; it plays the beat the cursor is on, as a key does once a run
   * is going. A tap just before the start plays the first beat.
   */
  private onTap() {
    if (this.running) {
      this.heardTap();
      this.cursor!.checkTiming();
      this.cursor?.onHoldNote();
      return;
    }
    if (this.cursor?.holdBeforeAutoStart()) this.heardTap();
  }

  // The pass is played: it is graded, missed beats included.
  private heardTap() {
    this.tapHeard();
    this.cursor?.startMarkingMissedNotes();
  }

  private onPressKey() {
    if (!this.running) {
      const [timing] = this.cursor!.tryStart(this.onExerciseEnd, this);
      if (timing === 'early') {
        this.showToast('Too early');
        this.beats[0]?.shake();
      } else if (timing === 'late') {
        this.showToast('Too late');
        this.beats[0]?.shake();
      } else {
        this.beatGraphics.forEach((graphic) => graphic.setColor(colors.beatNumberFillQuestion));
        this.running = true;
        this.suspendGuard.reset(); // MUS-552: baseline at play-start so frame 1 never pauses
        this.cursor?.onHoldNote();
        this.dispatchExerciseEvent({ type: 'NOTE_PLAYED', note: 0, velocity: 127 });
      }
    } else {
      this.cursor!.checkTiming();
      this.cursor?.onHoldNote();
    }
  }

  // ==================================================================
  // Exercise end — dispatches to machine
  // ==================================================================

  private onExerciseEnd() {
    this.dispatchExerciseEvent({ type: 'PHRASE_END' });
  }

  // ==================================================================
  // Note graphics helpers (INoteGraphicsDatabase)
  // ==================================================================

  getNoteById(id: string): IPlayable | null {
    return this.table.get(id) ?? null;
  }

  getNotePosition(id: string) {
    return this.getNoteById(id)?.getPosition().x ?? null;
  }

  getPhraseEndPosition() {
    return 0;
  }

  // ==================================================================
  // Scene stop / unload
  // ==================================================================

  stop() {
    this.scene.stop();
  }

  unload(): void {
    this.stopNativeMetronome();
    this.clear();
    this.cursor = undefined;
    super.unload();
  }

  restart(): void {
    this.history = [];
    this.dispatchExerciseEvent({ type: 'RETRY' });
  }
}
